import { apiClient } from './apiClient';
import { deviceFingerprintingService } from '../services/deviceFingerprinting';
import type { DeviceFingerprintData } from '../services/deviceFingerprinting';

/**
 * Session initiation request to backend
 */
export interface InitiateSessionRequest {
  bookId: string;
  deviceFingerprint: DeviceFingerprintRequestDto;
}

/**
 * Device fingerprint data for API request
 */
export interface DeviceFingerprintRequestDto {
  userAgent: string;
  screenResolution: string;
  timeZone: string;
  webGLFingerprint: string | null;
  audioFingerprint: string | null;
}

/**
 * Session initiation response from backend
 */
export interface SessionInitiationDto {
  sessionToken: string;
  expiresAt: string; // ISO 8601 datetime
  maxChunksPerMinute: number;
  totalChunks: number;
}

/**
 * Secure chunk response from backend
 */
export interface SecureChunkDto {
  chunkIndex: number;
  content: string; // Base64-encoded decrypted content
  contentType: string; // "text/html" or "application/pdf"
  nextChunkToken: string | null;
  chunkSize: number;
}

/**
 * Session validation response (heartbeat)
 */
export interface SessionValidationDto {
  isValid: boolean;
  expiresAt: string;
  secondsRemaining: number;
  chunksAccessed: number;
  warning: string | null;
}

/**
 * Session refresh response
 */
export interface SessionRefreshDto {
  sessionToken: string;
  expiresAt: string;
  refreshedAt: string;
  message: string;
}

/**
 * Service for DRM-protected content streaming.
 * Handles session management and secure chunk retrieval.
 */
class SecureReadingService {
  private baseUrl = '/api/app/secure-content';

  /**
   * Initiate a DRM-protected reading session.
   * Returns session token and metadata.
   */
  async initiateSession(bookId: string): Promise<SessionInitiationDto> {
    // Collect device fingerprint
    const fingerprintData = await deviceFingerprintingService.collectFingerprint();

    const request: InitiateSessionRequest = {
      bookId,
      deviceFingerprint: this.mapToDeviceFingerprintDto(fingerprintData),
    };

    const response = await apiClient.post<SessionInitiationDto>(
      `${this.baseUrl}/initiate-session`,
      request
    );

    return response.data;
  }

  /**
   * Retrieve a decrypted content chunk.
   * Requires active session token.
   */
  async getChunk(
    sessionToken: string,
    bookId: string,
    chunkIndex: number
  ): Promise<SecureChunkDto> {
    const response = await apiClient.get<SecureChunkDto>(
      `${this.baseUrl}/chunk/${chunkIndex}`,
      {
        params: {
          sessionToken,
          bookId,
        },
      }
    );

    return response.data;
  }

  /**
   * Validate session (heartbeat).
   * Should be called every 60 seconds to keep session alive.
   */
  async validateSession(sessionToken: string): Promise<SessionValidationDto> {
    const response = await apiClient.post<SessionValidationDto>(
      `${this.baseUrl}/validate-session`,
      null,
      {
        params: { sessionToken },
      }
    );

    return response.data;
  }

  /**
   * Refresh session to extend expiration.
   * Extends session by SessionExpirationHours (4 hours).
   */
  async refreshSession(sessionToken: string): Promise<SessionRefreshDto> {
    const response = await apiClient.post<SessionRefreshDto>(
      `${this.baseUrl}/refresh-session`,
      null,
      {
        params: { sessionToken },
      }
    );

    return response.data;
  }

  /**
   * Terminate active session.
   * Should be called when user closes reader or logs out.
   */
  async terminateSession(sessionToken: string): Promise<void> {
    await apiClient.post(
      `${this.baseUrl}/terminate-session`,
      null,
      {
        params: { sessionToken },
      }
    );
  }

  /**
   * Decode Base64 chunk content to bytes.
   */
  decodeChunkContent(base64Content: string): Uint8Array {
    const binaryString = atob(base64Content);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes;
  }

  /**
   * Convert chunk bytes to text (for EPUB HTML content).
   */
  chunkBytesToText(bytes: Uint8Array): string {
    const decoder = new TextDecoder('utf-8');
    return decoder.decode(bytes);
  }

  // Private helper methods

  private mapToDeviceFingerprintDto(data: DeviceFingerprintData): DeviceFingerprintRequestDto {
    return {
      userAgent: data.userAgent,
      screenResolution: data.screenResolution,
      timeZone: data.timeZone,
      webGLFingerprint: data.webGLFingerprint,
      audioFingerprint: data.audioFingerprint,
    };
  }
}

// Export singleton instance
export const secureReadingService = new SecureReadingService();
